import { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronRight, ExternalLink, Minus, Plus, ShoppingBag, X } from 'lucide-react';
import { CartProvider, useCart } from '@/components/cards/cart';
import { ProductSheet } from '@/components/cards/ProductSheet';
import { formatMoney } from '@/lib/money';
import { productFromLink, formatOptions, type Product } from '@/lib/products';
import { PAYMENT_META, type PaymentMethod } from '@/lib/payments';
import { templatePreviewData } from '@/lib/templatePreviewData';
import { VISUAL_TEMPLATES } from '@/lib/visualTemplates';
import { getTheme } from '@/lib/themes';
import { cn } from '@/lib/utils';

/**
 * A working shop, in the page. Real cart, real product sheet with options,
 * a checkout that stops at the pay screen (no database). Everything here
 * is the same code the product ships — the template data is the Maker
 * starter's sample products.
 */
export function ShopDemo({ className }: { className?: string }) {
  const data = useMemo(() => templatePreviewData(VISUAL_TEMPLATES.find((t) => t.id === 'maker')!), []);
  const products = useMemo(
    () =>
      data.links
        .filter((l) => l.type === 'product')
        .map(productFromLink)
        .map((p, i) =>
          i === 1
            ? {
                ...p,
                badges: ['Popular'],
                bullets: ['60 minutes in the studio', '10 edited frames delivered in a week', 'Weekends available'],
                options: [
                  { id: 'when', name: 'When', type: 'buttons' as const, values: ['Weekday', 'Weekend'] },
                  { id: 'style', name: 'Style', type: 'dropdown' as const, values: ['Natural light', 'Studio flash', 'Both'] },
                ],
              }
            : { ...p, badges: p.badges.length ? p.badges : ['Limited'] },
        ),
    [data.links],
  );
  const theme = getTheme('cream');

  return (
    <CartProvider cardId="demo" products={products} persist={false}>
      <DemoInner products={products} theme={theme} className={className} />
    </CartProvider>
  );
}

function DemoInner({
  products,
  theme,
  className,
}: {
  products: Product[];
  theme: ReturnType<typeof getTheme>;
  className?: string;
}) {
  const cart = useCart()!;
  const [active, setActive] = useState<Product | null>(null);
  const [step, setStep] = useState<'browse' | 'checkout' | 'pay' | 'done'>('browse');
  const [method, setMethod] = useState<PaymentMethod>('venmo');
  const border = 'rgba(10,10,10,0.08)';

  return (
    <div className={cn('overflow-hidden rounded-[28px] border border-bone-deep', className)} style={{ backgroundColor: theme.pageBg, color: theme.text }}>
      {/* Faux phone chrome */}
      <div className="flex items-center justify-between px-4 py-3">
        <span className="mk-kicker" style={{ opacity: 0.6 }}>
          Shop · live demo
        </span>
        <span className="mk-kicker" style={{ opacity: 0.6 }}>
          gethaphe.com/u/alex
        </span>
      </div>

      <div className="relative min-h-[520px] px-4 pb-4">
        <AnimatePresence mode="wait">
          {step === 'browse' && (
            <motion.div key="browse" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <ul className="grid grid-cols-2 gap-3">
                {products.map((p) => {
                  const qty = cart.qtyOf(p.id);
                  const hasOptions = p.options.some((o) => o.values.length > 0);
                  return (
                    <li key={p.id} className="flex flex-col overflow-hidden rounded-xl border" style={{ backgroundColor: theme.cardBg, borderColor: border }}>
                      <button type="button" onClick={() => setActive(p)} className="relative aspect-square w-full overflow-hidden text-left" style={{ backgroundColor: theme.accent + '14' }}>
                        {p.imageUrl ? (
                          <img src={p.imageUrl} alt={p.name} className="h-full w-full object-cover" loading="lazy" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center" style={{ color: theme.accent }}>
                            <ShoppingBag className="h-8 w-8 opacity-70" />
                          </div>
                        )}
                        <div className="absolute left-2 top-2 flex gap-1">
                          {p.badges.map((b) => (
                            <span key={b} className="rounded-full px-2 py-0.5 font-mono text-[9px] font-medium uppercase tracking-widest" style={{ backgroundColor: theme.accent, color: theme.cardBg }}>
                              {b}
                            </span>
                          ))}
                        </div>
                      </button>
                      <div className="flex flex-1 flex-col p-3">
                        <button type="button" onClick={() => setActive(p)} className="text-left">
                          <p className="line-clamp-2 text-sm font-medium leading-snug tracking-tight">{p.name}</p>
                          {p.description && <p className="mt-1 line-clamp-2 text-[11px] leading-snug" style={{ opacity: 0.65 }}>{p.description}</p>}
                        </button>
                        <div className="mt-auto flex items-center justify-between pt-3">
                          <span className="text-sm font-medium tabular-nums">{formatMoney(p.priceCents)}</span>
                          {qty === 0 || hasOptions ? (
                            <button type="button" onClick={() => (hasOptions ? setActive(p) : cart.add(p))} aria-label={`Add ${p.name}`} className="flex h-8 min-w-8 items-center justify-center rounded-full px-2" style={{ backgroundColor: theme.accent, color: theme.cardBg }}>
                              <Plus className="h-4 w-4" />
                              {qty > 0 && <span className="ml-1 font-mono text-[10px]">{qty}</span>}
                            </button>
                          ) : (
                            <div className="flex h-8 items-center rounded-full border" style={{ borderColor: theme.accent + '80' }}>
                              <button type="button" onClick={() => cart.setQty(p.id, qty - 1)} className="flex h-8 w-7 items-center justify-center" aria-label="Remove one"><Minus className="h-3.5 w-3.5" /></button>
                              <span className="w-4 text-center text-xs font-medium tabular-nums">{qty}</span>
                              <button type="button" onClick={() => cart.add(p)} className="flex h-8 w-7 items-center justify-center" aria-label="Add one"><Plus className="h-3.5 w-3.5" /></button>
                            </div>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-4 text-center text-[11px]" style={{ opacity: 0.55 }}>
                Tap a product for the detail sheet. Add a few. This is the real component.
              </p>
            </motion.div>
          )}

          {step === 'checkout' && (
            <motion.div key="checkout" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="rounded-2xl bg-bone p-4 text-ink">
              <div className="flex items-center justify-between">
                <p className="text-lg font-medium tracking-tight">Order from Alex</p>
                <button onClick={() => setStep('browse')} aria-label="Back" className="rounded-full p-1 text-ink-muted hover:text-ink"><X className="h-4 w-4" /></button>
              </div>
              <ul className="mt-3 divide-y divide-bone-deep overflow-hidden rounded-xl border border-bone-deep bg-surface">
                {cart.lines.map((l) => (
                  <li key={l.key} className="flex items-center gap-3 p-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{l.product.name}</p>
                      {formatOptions(l.options) && <p className="truncate text-[11px] text-ink-soft">{formatOptions(l.options)}</p>}
                    </div>
                    <span className="font-mono text-xs text-ink-muted">×{l.qty}</span>
                    <span className="text-sm font-medium tabular-nums">{formatMoney(l.product.priceCents * l.qty)}</span>
                  </li>
                ))}
                <li className="flex items-center justify-between bg-bone-warm/60 px-3 py-2.5">
                  <span className="mk-kicker text-ink-muted">Total</span>
                  <span className="text-base font-medium tabular-nums">{formatMoney(cart.subtotal)}</span>
                </li>
              </ul>
              <p className="mk-kicker mt-4 text-ink-muted">Pay with</p>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {(['stripe', 'venmo', 'cashapp', 'paypal', 'zelle', 'square', 'cash'] as PaymentMethod[]).map((m) => (
                  <button key={m} onClick={() => setMethod(m)} aria-pressed={method === m} className={cn('flex h-10 items-center justify-center gap-1.5 rounded-md border text-xs font-medium', method === m ? 'border-ink bg-ink text-bone' : 'border-bone-deep bg-surface')}>
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: PAYMENT_META[m].color }} />
                    {m === 'stripe' ? 'Card' : (PAYMENT_META[m].shortName ?? PAYMENT_META[m].name)}
                  </button>
                ))}
              </div>
              <button onClick={() => setStep(method === 'cash' ? 'done' : 'pay')} className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-md bg-action text-sm font-medium text-bone">
                Place order · {formatMoney(cart.subtotal)}
                <ChevronRight className="h-4 w-4" />
              </button>
            </motion.div>
          )}

          {step === 'pay' && (
            <motion.div key="pay" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="rounded-2xl bg-bone p-4 text-ink">
              <p className="mk-kicker text-ink-muted">Order #1042</p>
              <p className="mt-1 text-3xl font-medium tracking-tightest tabular-nums">{formatMoney(cart.subtotal)}</p>
              <p className="text-xs text-ink-muted">to Alex Carter</p>
              <div className="mt-4 flex h-12 items-center justify-center gap-2 rounded-full text-sm font-medium text-white" style={{ backgroundColor: PAYMENT_META[method].color }}>
                <ExternalLink className="h-4 w-4" />
                {method === 'stripe' ? 'Pay by card · Apple Pay · Google Pay' : `Pay with ${PAYMENT_META[method].name}`}
              </div>
              <div className="mt-3 rounded-lg border border-bone-deep bg-surface px-3 py-2">
                <p className="mk-kicker text-ink-muted">Memo</p>
                <p className="font-mono text-sm">Order #1042 — Alex Carter</p>
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-ink-muted">
                {method === 'stripe'
                  ? 'Card orders confirm themselves through Stripe. Alex gets the money, minus Stripe’s fee. Haphe takes nothing.'
                  : `The visitor pays Alex directly in ${PAYMENT_META[method].name} with the order number in the memo, taps “I’ve sent it”, and Alex confirms from the Orders inbox.`}
              </p>
              <button onClick={() => { setStep('done'); }} className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-md border border-bone-deep bg-surface text-sm font-medium">
                <Check className="h-4 w-4" />
                {method === 'stripe' ? 'Simulate the webhook' : "I've sent the payment"}
              </button>
            </motion.div>
          )}

          {step === 'done' && (
            <motion.div key="done" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} className="flex min-h-[400px] flex-col items-center justify-center rounded-2xl bg-bone p-6 text-center text-ink">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent text-bone"><Check className="h-5 w-5" /></span>
              <p className="mt-4 text-lg font-medium tracking-tight">Order #1042 is in.</p>
              <p className="mt-1 max-w-xs text-sm text-ink-muted">
                Both sides get an email. Alex sees it in Orders, marks it fulfilled, and the customer tracks it on a public order page.
              </p>
              <button onClick={() => { cart.clear(); setStep('browse'); }} className="mt-6 h-10 rounded-full border border-bone-deep px-4 text-sm font-medium">
                Run it again
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Cart bar */}
        {step === 'browse' && cart.count > 0 && (
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            onClick={() => setStep('checkout')}
            className="sticky bottom-3 mt-4 flex w-full items-center justify-between rounded-full px-4 py-3 shadow-[0_18px_40px_-20px_rgba(10,10,10,0.45)]"
            style={{ backgroundColor: theme.text, color: theme.cardBg }}
          >
            <span className="flex items-center gap-2 text-sm font-medium">
              <ShoppingBag className="h-4 w-4" />
              {cart.count} {cart.count === 1 ? 'item' : 'items'} · {formatMoney(cart.subtotal)}
            </span>
            <span className="mk-kicker flex items-center gap-1">Checkout <ChevronRight className="h-3.5 w-3.5" /></span>
          </motion.button>
        )}
      </div>

      <ProductSheet product={active} theme={theme} canOrder onClose={() => setActive(null)} />
    </div>
  );
}
